import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouteMatch, useHistory, Link } from 'react-router-dom';

import { FaFileArchive, FaPrint } from 'react-icons/fa';
import api from '../../../services/api';
import { useToast } from '../../../hooks/toast';
import { useAuth } from '../../../hooks/auth';

import Loading from '../../../components/Loading';
import AssinaturaDigital from '../../../components/AssinaturaDigital';

import detectarDispositivo from '../../../utils/detectarDispositivo';
import pegarLocalizacao from '../../../utils/pegarLocalizacao';
import getResponseErrors from '../../../utils/getResponseErrors';

import { formataCPF, formataCNPJ } from '../../../utils/funcoes';

import { Container, DadosSignatario, ContratoTexto, Rodape } from './styles';

interface IUrlParams {
  ConCodigo: string;
  PlaCodigo: string;
}

interface IContrato {
  ConCodigo: string;
  ConDataInicio: Date;
  ConDataFim: Date;
  ConValor: number;
  ConContrato: string;
  arquivo_url: string;
}

interface IPaciente {
  PacCodigo: number;
  PacPessoaFisica: boolean;
  PacNome: string;
  PacNomeSocial: string;
  PacCPF: string;
  PacCNPJ: string;
}

interface IDependente {
  DepResponsavel: boolean;
  DepTitular: boolean;
  DepDataExclusao?: Date | null;
  paciente: IPaciente;
}

interface IPlano {
  dependentes: IDependente[];
}

const AssinaturaDigitalPage: React.FC = () => {
  const { params } = useRouteMatch<IUrlParams>();
  const history = useHistory();
  const { addToast } = useToast();
  const { user } = useAuth();

  const ConCodigo = Number(params.ConCodigo);
  const PlaCodigo = Number(params.PlaCodigo);

  const [loading, setLoading] = useState(false);
  const [contratoAssinado, setContratoAssinado] = useState<string>();
  const [contrato, setContrato] = useState('');
  const [plano, setPlano] = useState<IPlano | undefined>(undefined);

  const handleHTML = useCallback((texto: string) => {
    return texto
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
  }, []);

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        const [respostaContrato, respostaPlano] = await Promise.all([
          api.get<IContrato>(`/planos/contrato/${ConCodigo}`),
          api.get<IPlano>(`/planos/${PlaCodigo}`),
        ]);

        setContratoAssinado(respostaContrato.data.arquivo_url || undefined);
        setContrato(handleHTML(respostaContrato.data.ConContrato));
        setPlano(respostaPlano.data);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar dados do contrato',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }
    if (ConCodigo > 0 && PlaCodigo > 0) loadDados();
  }, [ConCodigo, PlaCodigo, addToast, handleHTML]);

  const signatario = useMemo(() => {
    if (!plano) return undefined;
    return (
      plano.dependentes.find(d => d.DepResponsavel && !d.DepDataExclusao) ||
      plano.dependentes.find(d => d.DepTitular && !d.DepDataExclusao) ||
      plano.dependentes[0]
    );
  }, [plano]);

  const nomeSignatario = useMemo(() => {
    if (!signatario) return '';
    const { PacNome, PacNomeSocial } = signatario.paciente;
    return PacNomeSocial && PacNomeSocial.trim() !== ''
      ? PacNomeSocial
      : PacNome;
  }, [signatario]);

  const handleAssinar = useCallback(
    async (assinatura: string) => {
      setLoading(true);
      try {
        const dispositivo = detectarDispositivo();
        const localizacao = await pegarLocalizacao();

        const resposta = await api.post(
          `/planos/contrato/${ConCodigo}/assinar`,
          {
            assinatura,
            metadados: {
              nomeSignatario: signatario?.paciente.PacNome,
              documentoSignatario:
                signatario && signatario.paciente.PacPessoaFisica
                  ? formataCPF(signatario.paciente.PacCPF)
                  : formataCNPJ(signatario?.paciente.PacCNPJ || ''),
              sistemaOperacional: dispositivo.sistemaOperacional,
              modeloDispositivo: dispositivo.modeloDispositivo,
              isMobile: dispositivo.isMobile,
              latitude: localizacao.latitude,
              longitude: localizacao.longitude,
              dataHora: new Date().toISOString(),
              usuCodigo: user?.UsuCodigo,
            },
          },
        );

        addToast({
          title: 'Contrato assinado com sucesso',
          type: 'success',
        });
        setContratoAssinado(resposta.data.link);
        // history.push(`/cartaosaudedetalhe/${PlaCodigo}`);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Não foi possível salvar a assinatura',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [ConCodigo, addToast, signatario, user?.UsuCodigo],
  );

  return (
    <Container>
      {loading && <Loading />}

      {signatario && (
        <DadosSignatario>
          <strong>Assinado por:</strong>
          <span>{nomeSignatario}</span>
          <span>
            {signatario.paciente.PacPessoaFisica
              ? `CPF ${formataCPF(signatario.paciente.PacCPF)}`
              : `CNPJ ${formataCNPJ(signatario.paciente.PacCNPJ)}`}
          </span>
        </DadosSignatario>
      )}
      {!contratoAssinado && (
        <>
          <ContratoTexto>
            {contrato && (
              <span dangerouslySetInnerHTML={{ __html: contrato }} />
            )}
          </ContratoTexto>

          <Rodape>
            <AssinaturaDigital
              handleAssinar={handleAssinar}
              disabled={loading}
            />
          </Rodape>
        </>
      )}

      {contratoAssinado && (
        <>
          <h2 className="py-4">
            Clique no link para visualizar o contrato assinado
          </h2>
          <a
            className="border border-primary p-2 rounded"
            style={{ textAlign: 'center' }}
            href={contratoAssinado}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaFileArchive size={26} /> Ver Contrato
          </a>
        </>
      )}
    </Container>
  );
};

export default AssinaturaDigitalPage;
